import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cva, type VariantProps } from 'cva';
import { type FunctionComponent, type ReactNode, useCallback } from 'react';
import { type StrictOmit } from 'ts-essentials';
import { CloseIcon } from '../../icons/CloseIcon';
import { CircularButton } from '../CircularButton/CircularButton';
import styles from './AlertDialog.module.css';

const variant = cva({
  base: styles.content,
  defaultVariants: {
    padding: 'medium',
    size: 'medium',
  },
  variants: {
    padding: {
      large: styles.paddingLarge,
      medium: styles.paddingMedium,
      none: styles.paddingNone,
      small: styles.paddingSmall,
    },
    size: {
      large: styles.large,
      medium: styles.medium,
      small: styles.small,
    },
  },
});

export type AlertDialogProps = StrictOmit<
  AlertDialogPrimitive.AlertDialogProps,
  'onOpenChange'
> &
  VariantProps<typeof variant> & {
    children: ReactNode;
    footer?: ReactNode;
    onClose: () => void;
    title: string;
  };

export const AlertDialog: FunctionComponent<AlertDialogProps> = ({
  children,
  footer,
  onClose,
  padding,
  size,
  title,
  ...props
}) => {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <AlertDialogPrimitive.Root onOpenChange={handleOpenChange} {...props}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className={styles.overlay} />
        <AlertDialogPrimitive.Content className={variant({ padding, size })}>
          <div className={styles.titleContainer}>
            <AlertDialogPrimitive.Title asChild className={styles.title}>
              <div>{title}</div>
            </AlertDialogPrimitive.Title>
            <AlertDialogPrimitive.Cancel asChild onClick={onClose}>
              <CircularButton
                aria-label="Cancel"
                className={styles.closeButton}
              >
                <CloseIcon />
              </CircularButton>
            </AlertDialogPrimitive.Cancel>
          </div>
          <AlertDialogPrimitive.Description
            asChild
            className={styles.description}
          >
            {children}
          </AlertDialogPrimitive.Description>
          {footer && <div className={styles.footer}>{footer}</div>}
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
};
